import { Colors, IconNames16, ISelectOption, Menu, MenuItem, Select } from '@pinpointhq/thumbtack';
import axios from 'axios';
import * as React from 'react';
import { adminMapboxAutocompleteIndexPath } from '../../../javascript/application/ts_routes';
import { useDebounce } from '../../shared/hooks/useDebounce';

export interface MapboxAddress {
  formattedAddress: string;
  latitude: number;
  longitude: number;
  context: {
    // Typing these all as optional, as per Mapbox docs:
    // "Not all features are available or relevant in all parts of the world"
    address?: {
      address_number?: string;
      street_name?: string;
      name?: string;
    };
    neighborhood?: {
      name?: string;
    };
    postcode?: {
      name?: string;
    };
    locality?: {
      name?: string;
    };
    place?: {
      name?: string;
    };
    region?: {
      name?: string;
    };
    country?: {
      country_code?: string;
    };
  };
  name?: string;
}

export interface MapboxPlaceAutoCompleteProps {
  onPlaceSelect: (place: MapboxAddress | null) => void;
  selectedOption: ISelectOption | null;
  setSelectedOption: (option: ISelectOption | null) => void;
  placeholder?: string;
  restrictedCountry?: string;
}

// See https://docs.mapbox.com/api/search/geocoding/#geographic-feature-types for descriptions on the what each field really means
export const extractAddressComponents = (place: MapboxAddress, isUnitedStates: boolean) => {
  const { context } = place;

  const addressNumber = context.address?.address_number || '';
  const streetName = context.address?.street_name || '';

  const autocompletedAddressOne = [addressNumber, streetName].join(' ').trim() || place.name || '';
  const autocompletedAddressTwo = context.neighborhood?.name || '';

  const autocompletedTown = isUnitedStates
    ? context.region?.name || ''
    : context.place?.name || context.locality?.name || '';

  const autocompletedPostcode = context.postcode?.name || '';
  const autocompletedCountry = context.country?.country_code || '';

  return {
    autocompletedAddressOne,
    autocompletedAddressTwo,
    autocompletedTown,
    autocompletedPostcode,
    autocompletedCountry,
  };
};

export const MapboxPlaceAutoComplete = ({
  onPlaceSelect,
  selectedOption,
  setSelectedOption,
  placeholder,
  restrictedCountry,
}: MapboxPlaceAutoCompleteProps) => {
  const [isSearching, setIsSearching] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const [suggestions, setSuggestions] = React.useState<MapboxAddress[]>([]);

  const fetchSuggestions = async (searchQuery: string) => {
    if (!searchQuery.trim().length) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const params = new URLSearchParams({
        query: searchQuery,
        ...(restrictedCountry && { countryCode: restrictedCountry }),
      });

      const response = await axios.get(adminMapboxAutocompleteIndexPath(), { params });

      if (response.status === 200) {
        setSuggestions(response.data.addresses || []);
      } else {
        console.error('Failed to fetch suggestions');
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  useDebounce(inputValue, 500, (value) => {
    fetchSuggestions(value);
  });

  const onInputChange = React.useCallback((query: string) => {
    setInputValue(query);
    if (!query.trim().length) {
      setSuggestions([]);
    }
  }, []);

  const handleSuggestionClick = React.useCallback(
    (address: MapboxAddress) => {
      onPlaceSelect(address);
    },
    [onPlaceSelect],
  );

  const onItemSelect = (item: ISelectOption | null) => {
    if (!item) {
      setSelectedOption(null);
      onPlaceSelect(null);
      return;
    }

    setSelectedOption(item);
    const selectedAddress = suggestions.find((s) => s.formattedAddress === item.value);
    if (selectedAddress) {
      handleSuggestionClick(selectedAddress);
    }
  };

  const options: ISelectOption[] = suggestions.map((suggestion) => ({
    value: suggestion.formattedAddress,
    label: suggestion.formattedAddress,
    icon: IconNames16.MAP_PIN,
  }));

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;

    const queryWords = query
      .toLowerCase()
      .split(/[\s,]+/)
      .filter((word) => word.length > 0);

    const escapedWords = queryWords.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const combinedRegex = new RegExp(`(${escapedWords.join('|')})`, 'ig');

    return text.replace(combinedRegex, `<span style="color: ${Colors.GREEN_60}; font-weight: 500;">$1</span>`);
  };

  const renderMenu = ({ items }: { items: ISelectOption[] }) => {
    return (
      <Menu>
        {items.map((item) => {
          const highlightedLabel = highlightText(item.label, inputValue);
          return (
            <MenuItem
              key={item.value}
              icon={IconNames16.MAP_PIN}
              onClick={() => onItemSelect(item)}
              text={<span dangerouslySetInnerHTML={{ __html: highlightedLabel }} />}
            />
          );
        })}
      </Menu>
    );
  };

  return (
    <Select
      // The filtering is already handled server-side by the Mapbox API based on the search query
      itemPredicate={() => true}
      itemListRenderer={renderMenu}
      placeholder={placeholder || 'Start typing an address'}
      inputProps={{ placeholder: 'E.g. 1 Oxford St, London, UK', style: { minWidth: '300px' } }}
      fill={true}
      buttonProps={{ large: true }}
      activeItem={selectedOption}
      onQueryChange={onInputChange}
      items={options}
      onItemSelect={onItemSelect}
      clearable={true}
      loading={isSearching}
    />
  );
};
